import { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { bgnToEur, eurToBgn } from '../../domain/currency';
import {
  calculateYearlyExpenses,
  consumptionPer100km,
  pickFuelPricePerLiter,
  type EngineSpec,
} from '../../domain/expenses';
import { useFuelPrices } from '../../hooks/useFuelPrices';

interface CalculatorProps {
  engine: EngineSpec | null;
  /** Average asking price (BGN) for the engine, used for the repairs and insurance estimates. */
  currentPrice: number | null;
  /** Why this engine is shown, e.g. that it's the most common one because none was picked. */
  engineNote?: string;
}

const DEFAULT_KMS_PER_YEAR = 15000;

function formatEur(bgn: number): string {
  return `${Math.round(bgnToEur(bgn)).toLocaleString()} €`;
}

/** Parses a number field; empty or invalid input is null rather than 0, so clearing a field doesn't zero the estimate. */
function parseInput(text: string): number | null {
  const value = Number(text.replace(',', '.'));
  return text.trim() !== '' && Number.isFinite(value) && value >= 0 ? value : null;
}

function CostRow({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="baseline" spacing={2} sx={{ py: 1 }}>
      <Box>
        <Typography>{label}</Typography>
        {hint && (
          <Typography variant="caption" color="text.secondary">
            {hint}
          </Typography>
        )}
      </Box>
      <Typography fontWeight={600} sx={{ whiteSpace: 'nowrap' }}>
        {value}
      </Typography>
    </Stack>
  );
}

export function ExpensesCalculator({ engine, currentPrice, engineNote }: CalculatorProps) {
  const [kmsText, setKmsText] = useState(String(DEFAULT_KMS_PER_YEAR));
  const [consumptionText, setConsumptionText] = useState('');
  const { data: fuelPrices } = useFuelPrices();

  // Prefill the spec's consumption whenever the engine changes; the buyer can overwrite it,
  // which is the only way to get a fuel estimate for engines whose spec has no figure.
  const specConsumption = engine ? consumptionPer100km(engine) : null;
  useEffect(() => {
    setConsumptionText(specConsumption !== null ? String(specConsumption) : '');
  }, [engine, specConsumption]);

  if (!engine) {
    return (
      <Box>
        <Typography variant="h6" gutterBottom>
          Yearly running costs
        </Typography>
        <Typography color="text.secondary">Pick an engine to see what it costs to run.</Typography>
      </Box>
    );
  }

  const kmsPerYear = parseInput(kmsText);
  const consumption = parseInput(consumptionText);

  // calculateYearlyExpenses works in BGN internally (like the rest of the domain layer) and
  // formats to EUR only at display, so a live EUR fuel price has to convert back to BGN here.
  // Falls back to the domain layer's own default price while fuelPrices hasn't loaded yet.
  const fuelPricePerLiterBgn = fuelPrices ? eurToBgn(pickFuelPricePerLiter(engine.fuelType, fuelPrices)) : undefined;
  const expenses =
    currentPrice !== null && kmsPerYear !== null
      ? calculateYearlyExpenses(engine, currentPrice, kmsPerYear, fuelPricePerLiterBgn, consumption)
      : null;

  return (
    <Box>
      <Typography variant="h6">Yearly running costs</Typography>
      <Typography variant="body2" color="text.secondary">
        {engine.engine}
        {engineNote ? ` · ${engineNote}` : ''}
      </Typography>

      <Stack direction="row" spacing={2} sx={{ my: 2 }}>
        <TextField
          label="Km per year"
          size="small"
          value={kmsText}
          onChange={(event) => setKmsText(event.target.value)}
          error={kmsPerYear === null}
          slotProps={{ htmlInput: { inputMode: 'numeric' } }}
          sx={{ flex: 1 }}
        />
        <TextField
          label="L/100 km"
          size="small"
          value={consumptionText}
          placeholder="e.g. 7.5"
          onChange={(event) => setConsumptionText(event.target.value)}
          helperText={specConsumption === null ? 'No official figure, enter yours' : undefined}
          slotProps={{ htmlInput: { inputMode: 'decimal' } }}
          sx={{ flex: 1 }}
        />
      </Stack>

      {currentPrice === null && (
        <Typography color="text.secondary">We don&apos;t have a market price for this engine yet.</Typography>
      )}

      {expenses && (
        <Stack divider={<Divider flexItem />}>
          <CostRow
            label="Fuel"
            value={expenses.fuelCost !== null ? formatEur(expenses.fuelCost) : '—'}
            hint={
              expenses.fuelLiters !== null
                ? `${Math.round(expenses.fuelLiters).toLocaleString()} litres a year`
                : 'Enter a consumption to estimate fuel'
            }
          />
          <CostRow
            label="Vehicle tax"
            value={expenses.governmentTax !== null ? formatEur(expenses.governmentTax) : '—'}
            hint="Sofia rates"
          />
          <CostRow label="Insurance" value={formatEur(expenses.insurance)} hint="Optional casco, about 5% of the price" />
          <CostRow label="Total per year" value={formatEur(expenses.total)} />
          <CostRow
            label="Plus repairs, roughly"
            value={formatEur(expenses.repairs)}
            hint="About 3% of the price; depends heavily on the car"
          />
        </Stack>
      )}
    </Box>
  );
}
